/**
 * Token Manager - Authentication State Management
 *
 * The backend sets the sign-in token as an httpOnly cookie, but the API is on a different
 * site (Railway) from the app, so browsers don't send that cookie. Until the API moves to
 * api.adgrowai.com, the token is also kept in localStorage and sent as a Bearer header.
 * The user data kept here for the UI must never hold credentials (see withoutSecrets).
 */

const USER_KEY = 'userData';
const AUTH_TOKEN_KEY = 'authToken';

// The backend used to send Google and Meta credentials (and a few server-only fields) with
// the user record, and this file saved them in local storage. It no longer sends them, but
// copies saved before that are still in browsers, so they are removed on every save and
// once when the app loads. Old client-side OAuth code also left tokens under these keys.
const SERVER_ONLY_FIELDS = ['password', 'emailVerificationToken', 'emailVerificationExpires', 'passwordResetToken',
  'passwordResetExpires', 'loginAttempts', 'lockUntil', 'tokenVersion'];
const LEGACY_TOKEN_KEYS = ['meta_token_info', 'google_token_info'];

const withoutSecrets = (userData) => {
  const safe = { ...userData };
  SERVER_ONLY_FIELDS.forEach((field) => delete safe[field]);
  if (safe.googleAds) {
    const { refreshToken, accessToken, ...googleAds } = safe.googleAds;
    safe.googleAds = googleAds;
  }
  if (safe.metaAds) {
    const { accessToken, ...metaAds } = safe.metaAds;
    safe.metaAds = metaAds;
  }
  return safe;
};

const scrubStoredSecrets = () => {
  try {
    LEGACY_TOKEN_KEYS.forEach((key) => localStorage.removeItem(key));
    const stored = localStorage.getItem(USER_KEY);
    if (stored) localStorage.setItem(USER_KEY, JSON.stringify(withoutSecrets(JSON.parse(stored))));
  } catch (error) {
    // Unreadable stored data: drop it rather than keep secrets around.
    localStorage.removeItem(USER_KEY);
  }
};
scrubStoredSecrets();

/**
 * saveToken - Saves JWT to localStorage for cross-domain Authorization header use.
 * Token is also set as httpOnly cookie by backend (same-domain fallback).
 */
export const saveToken = (token) => {
  if (token) {
    localStorage.setItem(AUTH_TOKEN_KEY, token);
  }
};

/**
 * getToken - Returns JWT from localStorage for Authorization header.
 */
export const getToken = () => {
  return localStorage.getItem(AUTH_TOKEN_KEY);
};

/**
 * Remove auth state (logout)
 */
export const removeToken = () => {
  localStorage.removeItem(USER_KEY);
  localStorage.removeItem(AUTH_TOKEN_KEY);
  localStorage.removeItem('userId');
  localStorage.removeItem('userEmail');
  window.dispatchEvent(new Event('auth-context-changed'));
};

/**
 * Save user data to localStorage (non-sensitive UI info only)
 * @param {Object} userData - User information
 */
export const saveUserData = (userData) => {
  if (userData) {
    localStorage.setItem(USER_KEY, JSON.stringify(withoutSecrets(userData)));
    if (userData._id) {
      localStorage.setItem('userId', userData._id);
    }
    if (userData.email) {
      localStorage.setItem('userEmail', userData.email);
    }
    window.dispatchEvent(new Event('auth-context-changed'));
  }
};

/**
 * Merge changes into the stored user data (e.g. { isEmailVerified: true } once verified).
 */
export const updateUserData = (changes) => {
  const current = getUserData();
  if (current) saveUserData({ ...current, ...changes });
};

/**
 * Get user data from localStorage
 * @returns {Object|null} - User data or null
 */
export const getUserData = () => {
  const userData = localStorage.getItem(USER_KEY);
  return userData ? JSON.parse(userData) : null;
};

/**
 * Check if user is authenticated.
 * Since we can't read the httpOnly cookie, we check for user data presence.
 * The server will reject requests with expired/invalid cookies (401).
 */
export const isAuthenticated = () => {
  return !!getUserData();
};

/**
 * Get user ID from stored user data
 * @returns {string|null} - User ID or null
 */
export const getUserId = () => {
  const userData = getUserData();
  return userData?._id || userData?.id || null;
};

/**
 * Decode JWT token payload without verification
 * @deprecated Token is now in httpOnly cookie and cannot be decoded client-side
 */
export const getTokenPayload = () => {
  return null;
};

/**
 * @deprecated Token expiration is handled server-side with httpOnly cookies
 */
export const getTokenExpiration = () => {
  return null;
};

/**
 * @deprecated Token expiration is handled server-side with httpOnly cookies
 */
export const isTokenExpiringSoon = () => {
  return false;
};

export default {
  saveToken,
  getToken,
  removeToken,
  saveUserData,
  updateUserData,
  getUserData,
  getTokenPayload,
  isAuthenticated,
  getUserId,
  getTokenExpiration,
  isTokenExpiringSoon
};
